import React, { useMemo, useState } from 'react';
import {
  Plus,
  DotsThree,
  Phone,
  ChatCircleText,
  CheckCircle,
  FileText,
  Bell,
  Warning,
  CurrencyDollar,
  ArrowUUpLeft,
  Calendar,
  Buildings,
  PencilSimple,
  ChatCircleDots,
  Handshake,
} from 'phosphor-react';
import { twMerge } from 'tailwind-merge';
import moment from 'moment';
import {
  CollectionActivity,
  CollectionActivityReference,
  CollectionActivityType,
  PROMISE_TO_PAY_TYPE_LABELS,
  PROMISE_TO_PAY_REASON_LABELS,
} from '@dill/dill-shared';
import LexicalViewer from '../../../general/LexicalEditor/LexicalViewer';
import AddNoteModal from '../../suppliers/CollectionActivityTimeline/modals/AddNoteModal/AddNoteModal';
import AddCallModal from '../../suppliers/CollectionActivityTimeline/modals/AddCallModal/AddCallModal';
import AddTaskModal from '../../suppliers/CollectionActivityTimeline/modals/AddTaskModal/AddTaskModal';
import EditNoteModal from '../../suppliers/CollectionActivityTimeline/modals/EditNoteModal/EditNoteModal';
import EditCallModal from '../../suppliers/CollectionActivityTimeline/modals/EditCallModal/EditCallModal';
import EditTaskModal from '../../suppliers/CollectionActivityTimeline/modals/EditTaskModal/EditTaskModal';
import { DillModal } from '../../../general/Dill/DillModal';
import { DillButton } from '../../../general/Dill/DillButton';

interface InvoiceActivitiesModalProps {
  open: boolean;
  handleClose: () => void;
  invoiceId: string;
  invoiceDocNumber: string;
  invoiceAmount?: number | null;
  buyerId: string;
  supplierId: string;
  activities: CollectionActivity[];
  onActivityChanged?: () => void;
}

const formatDuration = (seconds: number | undefined): string => {
  if (!seconds) return '';
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
};

const formatRelativeDate = (date: Date | string | undefined): string => {
  if (!date) return '';
  const m = moment(new Date(date));
  const now = moment();
  const diffHours = now.diff(m, 'hours');
  if (diffHours < 1) {
    const diffMins = now.diff(m, 'minutes');
    if (diffMins < 1) return 'Just now';
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return `Yesterday at ${m.format('h:mm A')}`;
  if (diffHours < 168) return m.format('ddd [at] h:mm A');
  return m.format('MMM D, YYYY [at] h:mm A');
};

const activityTypeConfig: Record<
  CollectionActivityType,
  { icon: React.ReactNode; color: string; ringColor: string; label: string }
> = {
  PAYMENT_REQUEST: {
    icon: <Bell size={16} weight="fill" />,
    color: 'bg-blue-600 text-white',
    ringColor: 'ring-blue-200',
    label: 'Payment Request',
  },
  PAYMENT_REMINDER: {
    icon: <Bell size={16} weight="fill" />,
    color: 'bg-blue-600 text-white',
    ringColor: 'ring-blue-200',
    label: 'Payment Reminder',
  },
  PAYMENT: {
    icon: <CurrencyDollar size={16} weight="fill" />,
    color: 'bg-emerald-600 text-white',
    ringColor: 'ring-emerald-200',
    label: 'Payment',
  },
  REFUND: {
    icon: <ArrowUUpLeft size={16} weight="fill" />,
    color: 'bg-orange-600 text-white',
    ringColor: 'ring-orange-200',
    label: 'Refund',
  },
  STATEMENT: {
    icon: <FileText size={16} weight="fill" />,
    color: 'bg-violet-600 text-white',
    ringColor: 'ring-violet-200',
    label: 'Statement',
  },
  DISPUTE: {
    icon: <Warning size={16} weight="fill" />,
    color: 'bg-rose-600 text-white',
    ringColor: 'ring-rose-200',
    label: 'Dispute',
  },
  PHONE_CALL: {
    icon: <Phone size={16} weight="fill" />,
    color: 'bg-amber-600 text-white',
    ringColor: 'ring-amber-200',
    label: 'Phone Call',
  },
  TEXT_MESSAGE: {
    icon: <ChatCircleDots size={16} weight="fill" />,
    color: 'bg-teal-600 text-white',
    ringColor: 'ring-teal-200',
    label: 'Text Message',
  },
  NOTE: {
    icon: <ChatCircleText size={16} weight="fill" />,
    color: 'bg-slate-500 text-white',
    ringColor: 'ring-slate-200',
    label: 'Note',
  },
  TASK: {
    icon: <CheckCircle size={16} weight="fill" />,
    color: 'bg-indigo-600 text-white',
    ringColor: 'ring-indigo-200',
    label: 'Task',
  },
  PROMISE_TO_PAY: {
    icon: <Handshake size={16} weight="fill" />,
    color: 'bg-cyan-600 text-white',
    ringColor: 'ring-cyan-200',
    label: 'Promise to Pay',
  },
};

function InvoiceActivitiesModal({
  open,
  handleClose,
  invoiceId,
  invoiceDocNumber,
  invoiceAmount,
  buyerId,
  supplierId,
  activities,
  onActivityChanged,
}: InvoiceActivitiesModalProps) {
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [isAddCallModalOpen, setIsAddCallModalOpen] = useState(false);
  const [isAddTaskModalOpen, setIsAddTaskModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<CollectionActivity | null>(null);
  const [editingCall, setEditingCall] = useState<CollectionActivity | null>(null);
  const [editingTask, setEditingTask] = useState<CollectionActivity | null>(null);
  const [addTypeMenuOpen, setAddTypeMenuOpen] = useState(false);

  const invoiceReferences: CollectionActivityReference[] = useMemo(() => {
    if (!invoiceDocNumber) {
      return [];
    }
    return [
      {
        id: invoiceId,
        type: 'INVOICE' as const,
        description: `INV-${invoiceDocNumber}`,
        amount: invoiceAmount,
      },
    ];
  }, [invoiceId, invoiceDocNumber, invoiceAmount]);

  const sortedActivities = useMemo(() => {
    return [...(activities || [])].sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      return dateB - dateA;
    });
  }, [activities]);

  const getContentString = (content: unknown): string | null => {
    if (!content) return null;
    if (typeof content === 'string') return content;
    if (typeof content === 'object') return JSON.stringify(content);
    return null;
  };

  const getActivityDescription = (activity: CollectionActivity): string => {
    switch (activity.activityType) {
      case 'PAYMENT':
        if (activity.paymentDetails) {
          const pd = activity.paymentDetails;
          // Bank payment-request payments settle asynchronously, so they are logged at initiation.
          const isBankInitiated = pd.paymentMethod === 'BANK' && !!pd.paymentRequestId;
          return `Payment of $${pd.amount.toFixed(2)} ${
            isBankInitiated ? 'initiated' : 'received'
          }`;
        }
        return 'Payment received';
      case 'REFUND':
        return activity.refundDetails
          ? `${
              activity.refundDetails.isPartialRefund ? 'Partial refund' : 'Refund'
            } of $${activity.refundDetails.amount.toFixed(2)} issued`
          : 'Refund issued';
      case 'PHONE_CALL':
        if (activity.callDetails) {
          const outcome = activity.callDetails.outcome || '';
          const duration = formatDuration(activity.callDetails.duration ?? undefined);
          const parts = [`Call to ${activity.callDetails.phoneNumber || ''}`, outcome];
          if (duration) parts.push(duration);
          return parts.join(' \u00B7 ');
        }
        return 'Phone call logged';
      case 'NOTE':
        return '';
      case 'TASK':
        return activity.taskDetails?.title || 'Task created';
      case 'DISPUTE':
        return activity.disputeDetails
          ? `Dispute ${activity.disputeDetails.status.toLowerCase()}`
          : 'Dispute updated';
      case 'PAYMENT_REQUEST':
      case 'PAYMENT_REMINDER':
        if (activity.reminderDetails) {
          const invoiceCount = activity.reminderDetails.invoiceIds?.length || 0;
          const invoiceText = invoiceCount === 1 ? '1 invoice' : `${invoiceCount} invoices`;
          const recipientText = activity.reminderDetails.recipientEmail
            ? ` to ${activity.reminderDetails.recipientEmail}`
            : '';
          const invoicePart = invoiceCount > 0 ? ` (${invoiceText})` : '';
          // Payment request lifecycle events use the `action` field.
          const action = activity.reminderDetails.action;
          if (action) {
            const channelText = ` via ${activity.reminderDetails.channel}`;
            switch (action) {
              case 'CREATED':
                return `Payment request created${channelText}${recipientText}`;
              case 'UPDATED':
                return `Payment request edited${channelText}${recipientText}`;
              case 'RESENT':
                return `Payment request resent${channelText}${recipientText}`;
              case 'DELETED':
                return 'Payment request deleted';
              default:
                break;
            }
          }
          return `${activity.reminderDetails.reminderType} sent via ${activity.reminderDetails.channel}${recipientText}${invoicePart}`;
        }
        return 'Reminder sent';
      case 'STATEMENT':
        if (activity.reminderDetails) {
          const recipientText = activity.reminderDetails.recipientEmail
            ? ` to ${activity.reminderDetails.recipientEmail}`
            : '';
          return `${activity.reminderDetails.reminderType} sent via ${activity.reminderDetails.channel}${recipientText}`;
        }
        return 'Statement sent';
      case 'PROMISE_TO_PAY': {
        if (!activity.promiseToPayDetails) return 'Promise to pay logged';
        const { type, amount, promisedPaymentDate, reason, reasonOther } =
          activity.promiseToPayDetails;
        const typeText = type ? PROMISE_TO_PAY_TYPE_LABELS[type] ?? type : 'Payment';
        const amountText = amount != null ? `$${amount.toFixed(2)}` : '';
        const dateText = promisedPaymentDate
          ? new Date(promisedPaymentDate).toLocaleDateString()
          : null;
        const reasonText =
          reason === 'OTHER' && reasonOther
            ? reasonOther
            : reason
            ? PROMISE_TO_PAY_REASON_LABELS[reason] ?? reason
            : null;
        return [
          `${typeText}${amountText ? ` of ${amountText}` : ''}`,
          dateText ? `by ${dateText}` : null,
          reasonText ? `· ${reasonText}` : null,
        ]
          .filter(Boolean)
          .join(' ');
      }
      default:
        return '';
    }
  };

  const handleAddSuccess = () => {
    onActivityChanged?.();
  };

  const handleEditSuccess = () => {
    onActivityChanged?.();
  };

  return (
    <>
      <DillModal
        open={open}
        onClose={handleClose}
        title="Collection Activities"
        subtitle={`Invoice #${invoiceDocNumber} \u00B7 ${sortedActivities.length} activit${
          sortedActivities.length === 1 ? 'y' : 'ies'
        }`}
        maxWidth="640px"
        contentClassName="flex-1 min-h-0 overflow-y-auto">
        {/* Add Activity toolbar */}
        <div className="flex items-center justify-end px-5 pt-3 pb-1">
          <div className="relative">
            <DillButton
              label="Add Activity"
              icon={<Plus size={14} />}
              onClick={() => setAddTypeMenuOpen(!addTypeMenuOpen)}
            />
            {addTypeMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setAddTypeMenuOpen(false)} />
                <div className="absolute right-0 mt-1 bg-white rounded-xl border border-gray-200/90 shadow-lg z-20 py-1 min-w-[140px]">
                  <button
                    onClick={() => {
                      setAddTypeMenuOpen(false);
                      setIsAddNoteModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors cursor-pointer">
                    <ChatCircleText size={14} />
                    Note
                  </button>
                  <button
                    onClick={() => {
                      setAddTypeMenuOpen(false);
                      setIsAddCallModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors cursor-pointer">
                    <Phone size={14} />
                    Call
                  </button>
                  <button
                    onClick={() => {
                      setAddTypeMenuOpen(false);
                      setIsAddTaskModalOpen(true);
                    }}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors cursor-pointer">
                    <CheckCircle size={14} />
                    Task
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content */}
        {sortedActivities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <ChatCircleText size={40} className="mb-3" />
            <p className="text-sm font-medium">No activities for this invoice</p>
            <p className="text-xs mt-1">Add a note, call, or task to get started.</p>
          </div>
        ) : (
          <div className="px-5 pb-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-[15px] top-3 bottom-3 w-px bg-gradient-to-b from-gray-200 via-gray-200 to-transparent"></div>

              {/* Activity items */}
              <div className="space-y-3">
                {sortedActivities.map((activity) => {
                  const config = activityTypeConfig[activity.activityType] || {
                    icon: <DotsThree size={16} weight="fill" />,
                    color: 'bg-slate-500 text-white',
                    ringColor: 'ring-slate-200',
                    label: activity.activityType,
                  };
                  const isManual =
                    activity.activityType === 'NOTE' ||
                    activity.activityType === 'PHONE_CALL' ||
                    activity.activityType === 'TASK';

                  return (
                    <div key={activity.id} className="relative flex gap-3.5 pl-10 group">
                      {/* Icon */}
                      <div
                        className={twMerge(
                          'absolute left-0 top-4 w-[30px] h-[30px] rounded-full flex items-center justify-center ring-2 ring-white shadow-sm',
                          config.color,
                          config.ringColor
                        )}>
                        {config.icon}
                      </div>

                      {/* Content */}
                      <div className="flex-1 bg-white border border-gray-200 rounded-xl transition-colors hover:border-blue-200 hover:bg-blue-50/30">
                        {/* Header row */}
                        <div className="flex items-center justify-between px-4 pt-3.5 pb-0">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="text-sm font-medium text-gray-900">
                              {config.label}
                            </span>
                            {activity.source === 'AUTOMATIC' && (
                              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded text-[10px] font-medium tracking-wide uppercase">
                                Auto
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0 ml-3">
                            <span
                              className="text-xs text-gray-400 tabular-nums"
                              title={
                                activity.date
                                  ? moment(new Date(activity.date)).format(
                                      'MMM D, YYYY [at] h:mm A'
                                    )
                                  : ''
                              }>
                              {formatRelativeDate(activity.date)}
                            </span>
                            {isManual && (
                              <button
                                onClick={() => {
                                  if (activity.activityType === 'NOTE') setEditingNote(activity);
                                  if (activity.activityType === 'PHONE_CALL')
                                    setEditingCall(activity);
                                  if (activity.activityType === 'TASK') setEditingTask(activity);
                                }}
                                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 opacity-1 group-hover:opacity-100 transition-all cursor-pointer bg-transparent border-none"
                                title={`Edit ${config.label.toLowerCase()}`}
                                aria-label={`Edit ${config.label.toLowerCase()}`}>
                                <PencilSimple size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Description */}
                        {getActivityDescription(activity) && (
                          <div className="px-4 pt-1">
                            <p className="text-sm text-gray-500">
                              {getActivityDescription(activity)}
                            </p>
                          </div>
                        )}

                        {/* Rich text content for notes */}
                        {activity.activityType === 'NOTE' && activity.noteDetails?.content && (
                          <div className="px-4 pt-3 pb-1">
                            <LexicalViewer
                              content={getContentString(activity.noteDetails.content) || ''}
                              className="text-sm text-gray-500"
                            />
                          </div>
                        )}

                        {/* Rich text content for call notes */}
                        {activity.activityType === 'PHONE_CALL' && activity.callDetails?.notes && (
                          <div className="px-4 pt-3 pb-1">
                            <LexicalViewer
                              content={getContentString(activity.callDetails.notes) || ''}
                              className="text-sm text-gray-500"
                            />
                          </div>
                        )}

                        {/* Rich text content for task description */}
                        {activity.activityType === 'TASK' && activity.taskDetails?.description && (
                          <div className="px-4 pt-3 pb-1">
                            <LexicalViewer
                              content={getContentString(activity.taskDetails.description) || ''}
                              className="text-sm text-gray-500"
                            />
                          </div>
                        )}

                        {/* Footer: task details, references */}
                        {((activity.activityType === 'TASK' && activity.taskDetails) ||
                          activity.references?.length) && (
                          <div className="mx-4 mt-3 pt-3 border-t border-gray-100 pb-3.5 space-y-2.5">
                            {/* Task details */}
                            {activity.activityType === 'TASK' && activity.taskDetails && (
                              <div className="flex flex-wrap gap-2">
                                {activity.taskDetails.dueDate && (
                                  <div className="flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg">
                                    <Calendar size={12} className="text-gray-400" />
                                    <span className="text-xs font-medium text-gray-500 tabular-nums">
                                      {moment(new Date(activity.taskDetails.dueDate)).format(
                                        'MMM D, YYYY'
                                      )}
                                    </span>
                                  </div>
                                )}
                                {activity.taskDetails.status && (
                                  <span
                                    className={twMerge(
                                      'text-xs font-medium px-2 py-0.5 rounded-full',
                                      activity.taskDetails.status === 'COMPLETED' &&
                                        'bg-emerald-50 text-emerald-700',
                                      activity.taskDetails.status === 'IN_PROGRESS' &&
                                        'bg-blue-50 text-blue-700',
                                      activity.taskDetails.status === 'PENDING' &&
                                        'bg-gray-50 text-gray-600',
                                      activity.taskDetails.status === 'CANCELLED' &&
                                        'bg-red-50 text-red-700'
                                    )}>
                                    {activity.taskDetails.status === 'IN_PROGRESS'
                                      ? 'In Progress'
                                      : activity.taskDetails.status.charAt(0) +
                                        activity.taskDetails.status.slice(1).toLowerCase()}
                                  </span>
                                )}
                                {activity.taskDetails.priority && (
                                  <span
                                    className={twMerge(
                                      'text-xs font-medium px-2 py-0.5 rounded-full',
                                      activity.taskDetails.priority === 'HIGH' &&
                                        'bg-red-50 text-red-700',
                                      activity.taskDetails.priority === 'MEDIUM' &&
                                        'bg-amber-50 text-amber-700',
                                      activity.taskDetails.priority === 'LOW' &&
                                        'bg-emerald-50 text-emerald-700'
                                    )}>
                                    {activity.taskDetails.priority.charAt(0) +
                                      activity.taskDetails.priority.slice(1).toLowerCase()}
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Reference chips */}
                            {activity.references && activity.references.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {activity.references.map((ref, idx) => (
                                  <span
                                    key={`${ref.type}-${ref.id}-${idx}`}
                                    className={twMerge(
                                      'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border',
                                      ref.type === 'INVOICE'
                                        ? 'bg-purple-50 text-purple-700 border-purple-200'
                                        : 'bg-blue-50 text-blue-500 border-blue-200'
                                    )}>
                                    {ref.type === 'INVOICE' ? (
                                      <FileText size={11} weight="fill" />
                                    ) : (
                                      <Buildings size={11} weight="fill" />
                                    )}
                                    {ref.description || ref.id}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Bottom padding when no footer */}
                        {!(
                          (activity.activityType === 'TASK' && activity.taskDetails) ||
                          activity.references?.length
                        ) && <div className="pb-3.5" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DillModal>

      {/* Add modals */}
      <AddNoteModal
        open={isAddNoteModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddNoteModalOpen(false)}
        onSuccess={handleAddSuccess}
        initialReferences={invoiceReferences}
      />
      <AddCallModal
        open={isAddCallModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddCallModalOpen(false)}
        onSuccess={handleAddSuccess}
        initialReferences={invoiceReferences}
      />
      <AddTaskModal
        open={isAddTaskModalOpen}
        buyerId={buyerId}
        onClose={() => setIsAddTaskModalOpen(false)}
        onSuccess={handleAddSuccess}
        initialReferences={invoiceReferences}
      />

      {/* Edit modals */}
      <EditNoteModal
        open={editingNote !== null}
        activity={editingNote}
        onClose={() => setEditingNote(null)}
        onSuccess={handleEditSuccess}
      />
      <EditCallModal
        open={editingCall !== null}
        activity={editingCall}
        onClose={() => setEditingCall(null)}
        onSuccess={handleEditSuccess}
      />
      <EditTaskModal
        open={editingTask !== null}
        activity={editingTask}
        onClose={() => setEditingTask(null)}
        onSuccess={handleEditSuccess}
      />
    </>
  );
}

export default InvoiceActivitiesModal;
